import type { LocalMaterialRecord } from '../types'

const DATABASE_NAME = 'studna-dashboard-lite'
const DATABASE_VERSION = 2
const STORE_NAME = 'materials'
const SETTINGS_STORE_NAME = 'settings'
const VAULT_HANDLE_KEY = 'obsidian-vault-handle'

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)

    request.onupgradeneeded = () => {
      const database = request.result

      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' })
        store.createIndex('created_at', 'created_at')
      }

      if (!database.objectStoreNames.contains(SETTINGS_STORE_NAME)) {
        database.createObjectStore(SETTINGS_STORE_NAME, { keyPath: 'key' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('无法打开本地资料库'))
  })
}

function runRequest<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('本地资料操作失败'))
  })
}

export async function listLocalMaterials() {
  const database = await openDatabase()

  try {
    const transaction = database.transaction(STORE_NAME, 'readonly')
    const records = await runRequest(
      transaction.objectStore(STORE_NAME).getAll() as IDBRequest<LocalMaterialRecord[]>,
    )
    return [...records].sort((left, right) =>
      right.created_at.localeCompare(left.created_at),
    )
  } finally {
    database.close()
  }
}

export async function saveLocalMaterial(record: LocalMaterialRecord) {
  const database = await openDatabase()

  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    await runRequest(transaction.objectStore(STORE_NAME).put(record))
  } finally {
    database.close()
  }
}

export async function deleteLocalMaterial(id: string) {
  const database = await openDatabase()

  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    await runRequest(transaction.objectStore(STORE_NAME).delete(id))
  } finally {
    database.close()
  }
}

export async function saveVaultDirectoryHandle(handle: unknown) {
  const database = await openDatabase()

  try {
    const transaction = database.transaction(SETTINGS_STORE_NAME, 'readwrite')
    await runRequest(
      transaction.objectStore(SETTINGS_STORE_NAME).put({
        key: VAULT_HANDLE_KEY,
        value: handle,
      }),
    )
  } finally {
    database.close()
  }
}

export async function loadVaultDirectoryHandle<T>() {
  const database = await openDatabase()

  try {
    const transaction = database.transaction(SETTINGS_STORE_NAME, 'readonly')
    const result = await runRequest<{ key: string; value: T } | undefined>(
      transaction.objectStore(SETTINGS_STORE_NAME).get(VAULT_HANDLE_KEY),
    )
    return result?.value ?? null
  } finally {
    database.close()
  }
}
